import { z } from "zod";
import { countCharacters } from "@root/shared/utils/countCharacters";
import { type Question } from "./types";

const MAX_TEXT_LENGTH = 1000;

const MESSAGES = {
  requiredSingle: "選択してください。",
  requiredMultiple: "必須項目です。少なくとも1つ選択してください。",
  requiredFollowUp: "その他の詳細をご記入ください。",
  tooLong: `${MAX_TEXT_LENGTH}文字以内でご入力ください。`,
} as const;

// single_choice は string、multiple_choice は string[]
export type ReopenEnqueteFormValues = Record<string, string | string[] | undefined>;

export const followUpKey = (questionCode: string): string => `${questionCode}__follow_up`;

// 選択値を選択肢コードの配列に揃える（single/multiple どちらでも扱えるように）
export const toSelectedCodes = (value: string | string[] | undefined): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value !== "") return [value];
  return [];
};

const isFollowUpRequired = (q: Question, values: ReopenEnqueteFormValues): boolean => {
  const selectedCodes = toSelectedCodes(values[q.questionCode]);
  return q.options.some(
    (opt) => opt.requiresFollowUpText && selectedCodes.includes(opt.optionCode),
  );
};

const isStringEmpty = (value: unknown): boolean =>
  typeof value !== "string" || value.trim().length === 0;

export const buildSchema = (questions: Question[]) => {
  const shape: Record<string, z.ZodType<string | string[]>> = {};

  questions.forEach((q) => {
    if (q.questionType === "multiple_choice") {
      shape[q.questionCode] = z.array(z.string()).default([]);
    } else {
      shape[q.questionCode] = z.string().default("");
    }
    if (q.options.some((opt) => opt.requiresFollowUpText)) {
      shape[followUpKey(q.questionCode)] = z.string().default("");
    }
  });

  return z.object(shape).check((ctx) => {
    const values = ctx.value as ReopenEnqueteFormValues;

    questions.forEach((q) => {
      if (q.required) {
        if (q.questionType === "multiple_choice") {
          const selected = values[q.questionCode];
          if (!Array.isArray(selected) || selected.length === 0) {
            ctx.issues.push({
              code: "custom",
              path: [q.questionCode],
              message: MESSAGES.requiredMultiple,
              input: values,
            });
          }
        } else if (isStringEmpty(values[q.questionCode])) {
          ctx.issues.push({
            code: "custom",
            path: [q.questionCode],
            message: MESSAGES.requiredSingle,
            input: values,
          });
        }
      }

      if (isFollowUpRequired(q, values)) {
        const followUp = values[followUpKey(q.questionCode)];
        if (isStringEmpty(followUp)) {
          ctx.issues.push({
            code: "custom",
            path: [followUpKey(q.questionCode)],
            message: MESSAGES.requiredFollowUp,
            input: values,
          });
        } else if (typeof followUp === "string" && countCharacters(followUp) > MAX_TEXT_LENGTH) {
          ctx.issues.push({
            code: "custom",
            path: [followUpKey(q.questionCode)],
            message: MESSAGES.tooLong,
            input: values,
          });
        }
      }
    });
  });
};

export type InputProps = z.infer<ReturnType<typeof buildSchema>>;
